import React from 'react';
import { Card, Tooltip, Row, Col, Tag } from 'antd';
import { SettingOutlined, RetweetOutlined, PlusCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

/**
 * Список колод. Раньше жил двумя файлами, Desktop и Mobile: они расходились ровно
 * отступом сетки и тремя подписями тултипов — остальные 220 строк совпадали дословно,
 * так что любая правка делалась дважды и половина подписей потерялась по дороге.
 */
/**
 * Бирка видимости: выделенная колода красится по своему виду — приватная бирюзовым,
 * публичная тёмно-красным, — невыделенная остаётся серой независимо от вида.
 */
const visibilityTagBackground = (visibility, selected) => {
    if (!selected) {
        return '#f0f0f0';
    }
    return visibility === 'PRIVATE' ? 'rgba(2,123,131,0.1)' : 'rgba(139,0,0,0.1)';
};

const visibilityTagColor = (visibility, selected) => {
    if (!selected) {
        return '#808080';
    }
    return visibility === 'PRIVATE' ? '#027b83' : '#8B0000';
};

/**
 * Флаг языка колоды. Флага может не быть: бэкенд знает двадцать девять языков,
 * а картинок у нас одиннадцать. Тогда показывается код языка — колоду на таком
 * языке всё равно надо видеть, а <img> без src рисует битую картинку.
 */
const LocaleFlag = ({ locale, options, title, dimmed }) => {
    if (!locale) {
        return null;
    }
    const known = options.find((option) => option.code === locale.code);
    const flag = known ? known.flag : locale.flag;
    const filter = dimmed ? 'grayscale(100%) opacity(75%)' : 'opacity(90%)';
    return (
        <Tooltip title={title}>
            {flag ? (
                <img
                    src={flag}
                    alt={locale.name}
                    style={{ width: '20px', height: '15px', borderRadius: '3px', filter }}
                />
            ) : (
                <span style={{ color: '#808080', fontSize: '12px' }}>{locale.code}</span>
            )}
        </Tooltip>
    );
};

const DeckList = ({
                      decks,
                      loading,
                      selectedDeckId,
                      isMobile,
                      onEdit,
                      onDelete,
                      onSetup,
                      onRowClick,
                      renderVisibility,
                      localeOptions,
                      translateLocaleOptions,
                  }) => {
    const navigate = useNavigate(); // Хук вызывается внутри компонента

    return (
        <Row gutter={[16, 16]} style={{ padding: isMobile ? 0 : '16px' }}>
            {decks.map((deck) => (
                <Col xs={24} sm={12} md={8} lg={6} key={deck.deckId}>
                    <Card
                        type="inner"
                        hoverable
                        onClick={() => onRowClick(deck)}
                        className="custom-card"
                        title={
                            <div
                                style={{
                                    fontSize: '18px',
                                    color: selectedDeckId === deck.deckId ? '#02796b' : '#8e949a',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {deck.title}
                            </div>
                        }
                        styles={{
                            header: {
                                backgroundColor: selectedDeckId === deck.deckId ? '#e5f9f6' : 'transparent',
                            },
                            body: {
                                padding: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            },
                        }}
                        style={{
                            boxShadow: 'none',
                            border: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            height: '100%',
                        }}
                        actions={[
                            <Tooltip title="Practice this deck" key="learn">
                                <PlayCircleOutlined
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRowClick(deck); // Активируем текущий deckId
                                        navigate('/learn'); // Переход на страницу /learn
                                    }}
                                    style={{
                                        fontSize: '16px',
                                        color: selectedDeckId === deck.deckId ? '#02796b' : '#808080',
                                    }}
                                />
                            </Tooltip>,
                            <Tooltip title="Add content" key="content">
                                <PlusCircleOutlined
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRowClick(deck); // Активируем текущий deckId
                                        navigate('/content'); // Переход на страницу /content
                                    }}
                                    style={{
                                        fontSize: '16px',
                                        color: selectedDeckId === deck.deckId ? '#02796b' : '#808080',
                                    }}
                                />
                            </Tooltip>,
                            <Tooltip title="Deck settings" key="settings">
                                <SettingOutlined
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(deck);
                                    }}
                                    style={{
                                        fontSize: '16px',
                                        color: selectedDeckId === deck.deckId ? '#02796b' : '#808080',
                                    }}
                                />
                            </Tooltip>,
                        ]}
                    >
                        <Card.Meta
                            description={
                                <div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                            <Tag
                                                bordered={false}
                                                color={selectedDeckId === deck.deckId ? 'rgba(238,96,2,0.1)' : '#f0f0f0'}
                                                style={{
                                                    color: selectedDeckId === deck.deckId ? '#ee6002' : '#808080',
                                                    height: '22px',
                                                    lineHeight: '22px',
                                                }}
                                            >
                                                {deck.type}
                                            </Tag>
                                        </div>
                                        <div style={{ width: '1px', backgroundColor: selectedDeckId === deck.deckId ? '#000000' : '#f0f0f0', height: '16px' }} />
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', marginLeft: '8px' }}>
                                            <Tag
                                                bordered={false}
                                                color={visibilityTagBackground(deck.visibility,
                                                    selectedDeckId === deck.deckId)}
                                                style={{
                                                    height: '22px',
                                                    lineHeight: '22px',
                                                    color: visibilityTagColor(deck.visibility,
                                                        selectedDeckId === deck.deckId),
                                                }}
                                            >
                                                {renderVisibility(deck.visibility)}
                                            </Tag>
                                        </div>
                                        <div style={{ width: '1px', backgroundColor: selectedDeckId === deck.deckId ? '#000000' : '#f0f0f0', height: '16px' }} />
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                            {deck.locale ? (
                                                <LocaleFlag
                                                    locale={deck.locale}
                                                    options={localeOptions}
                                                    title="Deck language"
                                                    dimmed={selectedDeckId !== deck.deckId}
                                                />
                                            ) : (
                                                <span style={{ color: '#808080' }}>N/A</span>
                                            )}
                                            {deck.translateLocale
                                                && deck.locale?.code !== deck.translateLocale.code && (
                                                <>
                                                    <RetweetOutlined style={{ color: '#808080', margin: '0 4px' }} />
                                                    <LocaleFlag
                                                        locale={deck.translateLocale}
                                                        options={translateLocaleOptions}
                                                        title="Translation Language"
                                                        dimmed={selectedDeckId !== deck.deckId}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <p
                                        style={{
                                            color: selectedDeckId === deck.deckId ? '#000000' : '#8e949a',
                                            display: '-webkit-box',
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            WebkitLineClamp: 4,
                                            minHeight: '88px',
                                        }}
                                    >
                                        {deck.description}
                                    </p>
                                </div>
                            }
                        />
                    </Card>
                </Col>
            ))}
        </Row>
    );
};

export default DeckList;
